import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { format, isToday, isTomorrow, isYesterday, differenceInHours, differenceInMinutes } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { notificationService } from '@/services/notificationService';

export interface Notification {
  id: string;
  type: 'appointment' | 'task' | 'payment' | 'document' | 'case' | 'reminder' | 'deadline';
  title: string;
  message: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  isRead: boolean;
  createdAt: string;
  scheduledFor?: string;
  relatedId?: string; // ID do compromisso, tarefa, etc.
  relatedType?: string; // Tipo do item relacionado
  icon?: string;
  actionUrl?: string;
}

/** Texto legível para tempo até o vencimento (evita "0 horas" com differenceInHours). */
function formatTimeUntilDue(dueDate: Date, now: Date): string {
  const ms = dueDate.getTime() - now.getTime();
  if (ms <= 0) return '';
  const totalMin = Math.max(0, Math.floor(ms / 60000));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  const timeLabel = dueDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  if (h === 0) return `${totalMin} minuto${totalMin === 1 ? '' : 's'} (às ${timeLabel})`;
  if (m === 0) return `${h} hora${h === 1 ? '' : 's'} (às ${timeLabel})`;
  return `${h}h ${m}min (às ${timeLabel})`;
}

// Avisos que a usuária limpou no sininho (por item: um compromisso novo volta a avisar)
const DISMISSED_KEY = 'notifications-dismissed-ids';
const readDismissed = (): string[] => {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const previousNotificationsRef = useRef<Notification[]>([]);
  const skipInitialPushRef = useRef(true);
  const [pushPermissionGranted, setPushPermissionGranted] = useState(false);

  const fetchAllNotifications = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Buscar compromissos próximos
      const { data: appointments } = await supabase
        .from('appointments')
        .select(`
          id,
          title,
          appointment_date,
          prospect_name,
          status,
          priority,
          appointment_type
        `)
        .eq('user_id', user.id)
        .gte('appointment_date', new Date().toISOString())
        .lte('appointment_date', new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString())
        .order('appointment_date', { ascending: true });

      // Buscar tarefas vencidas ou próximas do vencimento
      const { data: tasks } = await supabase
        .from('tasks')
        .select(`
          id,
          title,
          description,
          due_date,
          reminder_date,
          status,
          priority
        `)
        .eq('user_id', user.id)
        .in('status', ['Pendente', 'Em andamento'])
        .order('due_date', { ascending: true });

      // Buscar pagamentos pendentes
      const { data: payments } = await supabase
        .from('financial_entries')
        .select(`
          id,
          description,
          amount,
          payment_date,
          status,
          clients(name)
        `)
        .eq('user_id', user.id)
        .eq('status', 'Pendente')
        .not('payment_date', 'is', null)
        .order('payment_date', { ascending: true });

      // Gerar notificações baseadas nos dados
      const generatedNotifications: Notification[] = [];

      // Notificações de compromissos
      appointments?.forEach(appointment => {
        const appointmentDate = new Date(appointment.appointment_date);
        const now = new Date();
        const hoursUntil = differenceInHours(appointmentDate, now);

        if (hoursUntil <= 24 && hoursUntil >= 0) {
          generatedNotifications.push({
            id: `appointment-${appointment.id}`,
            type: 'appointment',
            title: `Lembrete: ${appointment.title}`,
            message: `Atendimento com ${appointment.prospect_name || 'cliente'} agendado para ${format(appointmentDate, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}`,
            priority: appointment.priority === 'urgente' ? 'urgent' : appointment.priority === 'alta' ? 'high' : 'normal',
            isRead: false,
            createdAt: new Date().toISOString(),
            scheduledFor: appointment.appointment_date,
            relatedId: appointment.id,
            relatedType: 'appointment',
            icon: 'calendar',
            actionUrl: `/agenda`
          });
        }
      });

      // Notificações de tarefas (alinhar ao lembrete: não avisar "próxima" 24h antes)
      tasks?.forEach(task => {
        if (!task.due_date) return;

        const dueDate = new Date(task.due_date);
        const now = new Date();
        const minutesUntil = differenceInMinutes(dueDate, now);

        if (minutesUntil < 0) {
          const lateMin = Math.abs(minutesUntil);
          const lateHours = Math.floor(lateMin / 60);
          const lateMsg =
            lateHours >= 1
              ? `cerca de ${lateHours} hora${lateHours === 1 ? '' : 's'}`
              : `${lateMin} minuto${lateMin === 1 ? '' : 's'}`;
          generatedNotifications.push({
            id: `task-overdue-${task.id}`,
            type: 'task',
            title: `Tarefa vencida: ${task.title}`,
            message: `A tarefa "${task.title}" venceu há ${lateMsg}`,
            priority: 'urgent',
            isRead: false,
            createdAt: new Date().toISOString(),
            relatedId: task.id,
            relatedType: 'task',
            icon: 'alert-triangle',
            actionUrl: `/tasks`
          });
        } else {
          let showUpcoming = false;
          if (task.reminder_date) {
            const rem = new Date(task.reminder_date);
            showUpcoming = now.getTime() >= rem.getTime() && minutesUntil >= 0;
          } else {
            showUpcoming = minutesUntil <= 120;
          }

          if (showUpcoming) {
            const dueStr = formatTimeUntilDue(dueDate, now);
            generatedNotifications.push({
              id: `task-due-${task.id}`,
              type: 'task',
              title: `Tarefa próxima do vencimento: ${task.title}`,
              message: `A tarefa "${task.title}" vence em ${dueStr}`,
              priority: 'high',
              isRead: false,
              createdAt: new Date().toISOString(),
              scheduledFor: task.due_date,
              relatedId: task.id,
              relatedType: 'task',
              icon: 'clock',
              actionUrl: `/tasks`
            });
          }
        }
      });

      // Notificações de pagamentos
      payments?.forEach(payment => {
        const paymentDate = new Date(payment.payment_date);
        const now = new Date();
        const daysUntil = Math.ceil((paymentDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        if (daysUntil <= 3 && daysUntil >= 0) {
          generatedNotifications.push({
            id: `payment-${payment.id}`,
            type: 'payment',
            title: `Pagamento pendente: ${payment.description}`,
            message: `Pagamento de R$ ${payment.amount?.toFixed(2)} vence em ${daysUntil} dias`,
            priority: daysUntil === 0 ? 'urgent' : 'high',
            isRead: false,
            createdAt: new Date().toISOString(),
            relatedId: payment.id,
            relatedType: 'payment',
            icon: 'dollar-sign',
            actionUrl: `/financial`
          });
        }
      });

      // Processos com movimentação nova (lida pelos e-mails), até o processo ser aberto
      const { data: movedCases } = await (supabase as any)
        .from('cases')
        .select('id, title, case_number_cnj, movement_update_at, movement_seen_at, last_movement_summary, movement_important')
        .eq('user_id', user.id)
        .not('movement_update_at', 'is', null);
      (movedCases || [])
        .filter((c: any) => !c.movement_seen_at || new Date(c.movement_seen_at) < new Date(c.movement_update_at))
        .forEach((c: any) => {
          const party = String(c.title || '').split(/ X /i)[0];
          generatedNotifications.push({
            id: `movement-${c.id}-${c.movement_update_at}`,
            type: 'case',
            title: c.movement_important ? `Decisão ou intimação: ${party}` : `Nova movimentação: ${party}`,
            message: [c.last_movement_summary, c.case_number_cnj].filter(Boolean).join(' · '),
            priority: c.movement_important ? 'urgent' : 'normal',
            isRead: false,
            createdAt: c.movement_update_at,
            relatedId: c.id,
            relatedType: 'case',
            actionUrl: `/processo/${c.id}`
          });
        });

      // Cadastros enviados pelo link, esperando revisão
      const { data: intakes } = await (supabase as any)
        .from('client_intakes')
        .select('id, label, submitted_at')
        .eq('status', 'enviado');
      (intakes || []).forEach((intake: any) => {
        generatedNotifications.push({
          id: `intake-${intake.id}`,
          type: 'reminder',
          title: `Novo cadastro: ${intake.label}`,
          message: 'Preencheu os dados e enviou os documentos. Revise para criar o cliente.',
          priority: 'high',
          isRead: false,
          createdAt: intake.submitted_at,
          relatedId: intake.id,
          relatedType: 'client',
          actionUrl: '/clients?entradas=1'
        });
      });

      // Ordenar notificações por prioridade e data (sem as que foram limpas)
      const dismissed = new Set(readDismissed());
      const sortedNotifications = generatedNotifications.filter((n) => !dismissed.has(n.id)).sort((a, b) => {
        const priorityOrder = { urgent: 0, high: 1, normal: 2, low: 3 };
        const aPriority = priorityOrder[a.priority] ?? 2;
        const bPriority = priorityOrder[b.priority] ?? 2;
        
        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }
        
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });

      setNotifications(sortedNotifications);
      setUnreadCount(sortedNotifications.filter(n => !n.isRead).length);

      // Detectar novas notificações para push (não disparar no primeiro carregamento da página)
      const previousNotifications = previousNotificationsRef.current;
      const newNotifications = sortedNotifications.filter(
        newNotif => !previousNotifications.some(
          prevNotif => prevNotif.id === newNotif.id
        )
      );

      if (skipInitialPushRef.current) {
        skipInitialPushRef.current = false;
      } else if (newNotifications.length > 0 && pushPermissionGranted) {
        notificationService.showBatchNotifications(newNotifications);
      }

      previousNotificationsRef.current = sortedNotifications;
    } catch (error) {
      console.error('Erro ao buscar notificações:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId: string) => {
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllAsRead = async () => {
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnreadCount(0);
  };

  const deleteNotification = async (notificationId: string) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  // Limpa o sininho: movimentações ficam como vistas no processo; o resto, lembrado neste aparelho
  const clearAll = async () => {
    const current = notifications;
    setNotifications([]);
    setUnreadCount(0);
    const ids = current.map((n) => n.id);
    try {
      localStorage.setItem(DISMISSED_KEY, JSON.stringify([...new Set([...readDismissed(), ...ids])].slice(-500)));
    } catch {
      // armazenamento indisponível: limpa só até a próxima atualização
    }
    const caseIds = ids
      .map((id) => id.match(/^movement-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})-/)?.[1])
      .filter(Boolean) as string[];
    if (caseIds.length) {
      await supabase.from('cases').update({ movement_seen_at: new Date().toISOString() } as any).in('id', caseIds);
    }
  };

  const getTimeAgo = (date: string) => {
    const now = new Date();
    const notificationDate = new Date(date);
    const minutesDiff = differenceInMinutes(now, notificationDate);
    
    if (minutesDiff < 1) return 'Agora mesmo';
    if (minutesDiff < 60) return `${minutesDiff} min atrás`;
    
    const hoursDiff = Math.floor(minutesDiff / 60);
    if (hoursDiff < 24) return `${hoursDiff}h atrás`;
    
    const daysDiff = Math.floor(hoursDiff / 24);
    if (daysDiff === 1) return 'Ontem';
    if (daysDiff < 7) return `${daysDiff} dias atrás`;
    
    return format(notificationDate, 'dd/MM/yyyy', { locale: ptBR });
  };

  useEffect(() => {
    fetchAllNotifications();
    
    // Solicitar permissão de notificações push
    const requestPushPermission = async () => {
      const granted = await notificationService.requestPermission();
      setPushPermissionGranted(granted);
    };

    // Aguardar um pouco antes de solicitar permissão para não ser muito agressivo
    const permissionTimeout = setTimeout(requestPushPermission, 3000);
    
    // Atualizar notificações a cada 5 minutos
    const interval = setInterval(fetchAllNotifications, 5 * 60 * 1000);
    
    return () => {
      clearTimeout(permissionTimeout);
      clearInterval(interval);
    };
  }, []);

  const requestPushPermission = async () => {
    const granted = await notificationService.requestPermission();
    setPushPermissionGranted(granted);
    return granted;
  };

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
    getTimeAgo,
    refreshNotifications: fetchAllNotifications,
    requestPushPermission,
    pushPermissionGranted,
    isPushSupported: notificationService.getIsSupported()
  };
}; 